import { useState, useEffect } from 'react';
import { Bell, Mail, MessageSquare, Save, AlertCircle, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../config/environment.js';
import { authFetch } from '../utils/authFetch';
import { getUserId } from '../utils/getUserId';
import { FEATURES } from '../config/features.js';

// ── Phone number validation (shared with NotificationPreferences) ─────────────

function normalizeToE164(phone) {
  if (!phone) return null;
  const stripped = phone.replace(/[^\d+]/g, '');
  if (stripped.startsWith('+')) {
    return stripped.length >= 11 && stripped.length <= 16 ? stripped : null;
  }
  if (stripped.length === 10) return `+1${stripped}`;
  if (stripped.length === 11 && stripped.startsWith('1')) return `+${stripped}`;
  return null;
}

// ── Helper: validate prerequisites before sending a test notification ────────
function validateTestPrerequisites(userAttributes, user, preferences) {
  const hasPhone = userAttributes?.phone_number && userAttributes.phone_number.trim() !== '';
  const hasEmail = userAttributes?.email || user?.signInDetails?.loginId;

  if (!hasPhone && !hasEmail) {
    return 'Please complete your profile with a phone number or email before testing notifications.';
  }
  if (preferences.smsEnabled && !hasPhone) {
    return 'SMS notifications are enabled but you haven\'t added a phone number yet. Go to your Profile to add one and complete SMS opt-in.';
  }
  return null;
}

// ── Helper: build the test notification request body ─────────────────────────
function buildTestNotificationBody(currentUserId, normalizedPhone, userAttributes, user) {
  return {
    userId: currentUserId,
    type: 'GATE_CHANGE',
    message: 'Test notification - Gate change for your flight',
    flightNumber: 'AA1234',
    phoneNumber: normalizedPhone || '',
    email: userAttributes?.email || user?.signInDetails?.loginId,
    flightInfo: {
      flightNumber: 'AA1234',
      departure: 'DFW',
      arrival: 'SEA',
      gate: 'A15',
      newGate: 'B22'
    }
  };
}

// ── Helper: build a user-facing message from the test notification result ────
function buildTestResultMessage(result, preferences) {
  const hasEmailResult = result.results?.email?.success;
  const hasSMSResult = result.results?.sms?.success;

  const emailExpected = FEATURES.EMAIL_NOTIFICATIONS_ENABLED && preferences.emailEnabled;
  const smsExpected = preferences.smsEnabled;

  const successParts = [];
  const failureParts = [];

  if (emailExpected) {
    if (hasEmailResult) {
      successParts.push('email');
    } else {
      failureParts.push('email may have failed - check your email address');
    }
  }

  if (smsExpected) {
    if (hasSMSResult) {
      successParts.push('SMS');
    } else {
      failureParts.push('SMS may have failed - check your phone number');
    }
  }

  if (successParts.length > 0) {
    let message = `Test notification${successParts.length > 1 ? 's' : ''} sent! Check your ${successParts.join(' and ')}.`;
    if (failureParts.length > 0) {
      message += ` (${failureParts.join(' and ')})`;
    }
    return { success: true, message };
  }

  return { success: false, message: 'Failed to send test notifications. Check your settings and contact information.' };
}

const NotificationsPage = ({ user, userAttributes }) => {
  const [preferences, setPreferences] = useState({
    emailEnabled: true,
    smsEnabled: true,
    flightChanges: true,
    gateChanges: true,
    delays: true,
    cancellations: true,
    boardingAlerts: true,
    tripUpdates: true
  });
  
  const [loading, setLoading] = useState(false);
  const [testingNotification, setTestingNotification] = useState(false);

  useEffect(() => {
    // Load saved preferences from API
    const userId = getUserId(user, userAttributes);
    
    if (userId) {
      loadPreferences(userId);
    }
  }, [user, userAttributes]);

  const loadPreferences = async (userId) => {
    try {
      const response = await authFetch(`${API_BASE_URL}/user-preferences/${userId}`);
      
      if (response.ok) {
        const data = await response.json();
        setPreferences(prev => ({
          ...prev,
          ...data.preferences,
          // Remove quietHours if it exists
          quietHours: undefined
        }));
      }
    } catch (error) {
      console.error('Error loading notification preferences:', error);
    }
  };

  const handlePreferenceChange = (key, value) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    
    const userId = getUserId(user, userAttributes);
    
    try {
      const response = await authFetch(`${API_BASE_URL}/user-preferences/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(preferences)
      });
      
      if (response.ok) {
        toast.success('Notification preferences saved successfully!');
      } else {
        const errorText = await response.text();
        console.error('Error saving preferences:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error(`Failed to save preferences: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleTestNotification = async () => {
    const prereqError = validateTestPrerequisites(userAttributes, user, preferences);
    if (prereqError) {
      toast.error(prereqError);
      return;
    }

    setTestingNotification(true);
    try {
      
      const currentUserId = getUserId(user, userAttributes);
      if (!currentUserId) {
        toast.error('You must be logged in to send a test notification');
        setTestingNotification(false);
        return;
      }

      // Only send SMS to the user's own verified Cognito phone number
      const normalizedPhone = normalizeToE164(userAttributes?.phone_number);

      // Use the existing test endpoint
      const response = await authFetch(`${API_BASE_URL}/flight-monitor/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(buildTestNotificationBody(currentUserId, normalizedPhone, userAttributes, user))
      });

      
      if (response.ok) {
        const result = await response.json();
        const { success, message } = buildTestResultMessage(result, preferences);
        if (success) {
          toast.success(message);
        } else {
          toast.error(message);
        }
      } else {
        const errorText = await response.text();
        console.error('Test notification error response:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
    } catch (error) {
      console.error('Error sending test notification:', error);
      toast.error(`Failed to send test notification: ${error.message}`);
    } finally {
      setTestingNotification(false);
    }
  };

  return (
    <div style={{
      padding: '32px',
      maxWidth: '800px',
      margin: '0 auto'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '32px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '8px'
        }}>
          <Bell size={32} style={{ color: '#dc2626' }} />
          <h1 style={{
            fontSize: '32px',
            fontWeight: '700',
            color: '#1f2937',
            margin: 0
          }}>
            Notification Preferences
          </h1>
        </div>
        <p style={{
          fontSize: '16px',
          color: '#6b7280',
          margin: 0
        }}>
          Manage how you receive travel notifications and updates
        </p>
      </div>

      {/* Delivery Methods */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
      }}>
        <h2 style={{
          fontSize: '20px',
          fontWeight: '600',
          color: '#1f2937',
          margin: '0 0 20px 0'
        }}>
          Delivery Methods
        </h2>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}>
          {FEATURES.EMAIL_NOTIFICATIONS_ENABLED && (
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              cursor: 'pointer',
              padding: '16px',
              borderRadius: '8px',
              backgroundColor: preferences.emailEnabled ? '#eff6ff' : 'white',
              border: `2px solid ${preferences.emailEnabled ? '#3b82f6' : '#e5e7eb'}`
            }}>
              <input
                type="checkbox"
                checked={preferences.emailEnabled}
                onChange={(e) => handlePreferenceChange('emailEnabled', e.target.checked)}
                style={{
                  margin: 0,
                  accentColor: '#dc2626',
                  transform: 'scale(1.2)'
                }}
              />
              <Mail size={24} style={{ color: '#dc2626' }} />
              <div>
                <div style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#1f2937'
                }}>
                  Email Notifications
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#6b7280'
                }}>
                  Receive notifications via email
                </div>
              </div>
            </label>
          )}

          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            cursor: 'pointer',
            padding: '16px',
            borderRadius: '8px',
            backgroundColor: preferences.smsEnabled ? '#eff6ff' : 'white',
            border: `2px solid ${preferences.smsEnabled ? '#3b82f6' : '#e5e7eb'}`
          }}>
            <input
              type="checkbox"
              checked={preferences.smsEnabled}
              onChange={(e) => handlePreferenceChange('smsEnabled', e.target.checked)}
              style={{
                margin: 0,
                accentColor: '#dc2626',
                transform: 'scale(1.2)'
              }}
            />
            <MessageSquare size={24} style={{ color: '#dc2626' }} />
            <div>
              <div style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1f2937'
              }}>
                SMS Notifications
              </div>
              <div style={{
                fontSize: '14px',
                color: '#6b7280'
              }}>
                Receive notifications via text message
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* Notification Types */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
      }}>
        <h2 style={{
          fontSize: '20px',
          fontWeight: '600',
          color: '#1f2937',
          margin: '0 0 20px 0',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <Bell size={20} />
          Notification Types
        </h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '16px'
        }}>
          {[
            { key: 'flightChanges', label: 'Flight Changes', icon: <MessageSquare size={18} /> },
            { key: 'gateChanges', label: 'Gate Changes', icon: <AlertCircle size={18} /> },
            { key: 'delays', label: 'Flight Delays', icon: <Clock size={18} /> },
            { key: 'cancellations', label: 'Cancellations', icon: <AlertCircle size={18} /> },
            { key: 'boardingAlerts', label: 'Boarding Alerts', icon: <Bell size={18} /> },
            { key: 'tripUpdates', label: 'Trip Updates', icon: <Clock size={18} /> }
          ].map(({ key, label, icon }) => (
            <label key={key} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              cursor: 'pointer',
              padding: '12px',
              borderRadius: '8px',
              backgroundColor: preferences[key] ? '#eff6ff' : 'white',
              border: `1px solid ${preferences[key] ? '#3b82f6' : '#e5e7eb'}`
            }}>
              <input
                type="checkbox"
                checked={preferences[key]}
                onChange={(e) => handlePreferenceChange(key, e.target.checked)}
                style={{
                  margin: 0,
                  accentColor: '#dc2626',
                  transform: 'scale(1.1)'
                }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {icon}
                <span style={{
                  fontSize: '16px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  {label}
                </span>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Test Notification */}
      <div style={{
        backgroundColor: '#fef2f2',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        border: '1px solid #fecaca'
      }}>
        <h2 style={{
          fontSize: '20px',
          fontWeight: '600',
          color: '#dc2626',
          margin: '0 0 8px 0'
        }}>
          Test Notifications
        </h2>
        <p style={{
          fontSize: '16px',
          color: '#6b7280',
          margin: '0 0 16px 0'
        }}>
          Send a test notification to verify your settings are working correctly.
        </p>
        <button
          onClick={handleTestNotification}
          disabled={testingNotification}
          style={{
            padding: '12px 24px',
            backgroundColor: testingNotification ? '#9ca3af' : '#dc2626',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '500',
            cursor: testingNotification ? 'not-allowed' : 'pointer',
            opacity: testingNotification ? 0.7 : 1
          }}
        >
          {testingNotification ? 'Sending...' : 'Send Test Notification'}
        </button>
      </div>

      {/* Save Button */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end'
      }}>
        <button
          onClick={handleSave}
          disabled={loading}
          style={{
            padding: '16px 32px',
            background: 'linear-gradient(135deg, #dc2626 0%, #1e40af 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <Save size={18} />
          {loading ? 'Saving...' : 'Save Preferences'}
        </button>
      </div>
    </div>
  );
};

export default NotificationsPage;
